import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Brain, Users, BookHeart, GraduationCap, Home, Sparkles, Camera, Globe, Rainbow } from 'lucide-react';

const CATEGORY_DATA = {
  'Sexual Health': {
    icon: Heart,
    color: 'from-pink-500 to-rose-500',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-500',
    textColor: 'text-pink-700',
    emoji: '❤️'
  },
  'Menstrual Health': {
    icon: Heart,
    color: 'from-red-400 to-pink-400',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-400',
    textColor: 'text-red-700',
    emoji: '🌸'
  },
  'Bullying & Safety': {
    icon: Users,
    color: 'from-orange-500 to-amber-500',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-500',
    textColor: 'text-orange-700',
    emoji: '🛡️'
  },
  'Mental Health': {
    icon: Brain,
    color: 'from-purple-500 to-indigo-500',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-500',
    textColor: 'text-purple-700',
    emoji: '🧠'
  },
  'Academic Pressure': {
    icon: GraduationCap,
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-700',
    emoji: '📚'
  },
  'Family & Home': {
    icon: Home,
    color: 'from-green-500 to-emerald-500',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-500',
    textColor: 'text-green-700',
    emoji: '🏠'
  },
  'Relationships': {
    icon: Heart,
    color: 'from-red-500 to-pink-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-500',
    textColor: 'text-red-700',
    emoji: '💕'
  },
  'Body Image': {
    icon: Sparkles,
    color: 'from-yellow-500 to-orange-500',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-500',
    textColor: 'text-yellow-700',
    emoji: '✨'
  },
  'Social Media': {
    icon: Camera,
    color: 'from-indigo-500 to-purple-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-500',
    textColor: 'text-indigo-700',
    emoji: '📱'
  },
  'Identity': {
    icon: Rainbow,
    color: 'from-rainbow-start to-rainbow-end',
    bgColor: 'bg-gradient-to-r from-red-100 via-yellow-100 to-purple-100',
    borderColor: 'border-purple-400',
    textColor: 'text-purple-700',
    emoji: '🌈'
  }
};

const CategoryExplorer = ({ scenarios, completedScenarios, onCategoryClick }) => {
  // Get category stats
  const getCategoryStats = (categoryName) => {
    const categoryScenarios = scenarios.filter(s => s.category === categoryName);
    const completed = categoryScenarios.filter(s => completedScenarios?.includes(s.id)).length;
    const total = categoryScenarios.length;
    return { completed, total };
  };

  const newCategories = Object.keys(CATEGORY_DATA).filter(cat => {
    const stats = getCategoryStats(cat);
    return stats.total > 0;
  });

  if (newCategories.length === 0) return null;

  return (
    <div className="mb-8">
      <h3 className="text-2xl font-black text-ssdp-navy mb-4 font-heading flex items-center gap-2">
        <Sparkles className="w-6 h-6 text-ssdp-teal" />
        Explore All Topics
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {newCategories.map((category, index) => {
          const data = CATEGORY_DATA[category];
          const Icon = data.icon;
          const stats = getCategoryStats(category);
          const progress = stats.total > 0 ? (stats.completed / stats.total) * 100 : 0;

          return (
            <motion.button
              key={category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ scale: 1.05, y: -4 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onCategoryClick(category)}
              className={`${data.bgColor} ${data.borderColor} border-3 rounded-2xl p-4 text-left shadow-[3px_3px_0px_0px_rgba(30,58,89,1)] hover:shadow-[5px_5px_0px_0px_rgba(30,58,89,1)] transition-all`}
              data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl">{data.emoji}</span>
                {stats.completed > 0 && (
                  <span className="text-xs font-bold bg-ssdp-teal text-white px-2 py-0.5 rounded-full font-heading">
                    {stats.completed}/{stats.total}
                  </span>
                )}
              </div>
              <h4 className={`text-sm font-bold ${data.textColor} mb-1 font-heading leading-tight`}>
                {category}
              </h4>
              {stats.total > 0 && (
                <div className="w-full bg-white/50 rounded-full h-1.5 overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${data.color} transition-all duration-500`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryExplorer;
